import { useState, useEffect, FC } from "react";
import { Card, Col, Row, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import SakhadDataTypeCard from "../../components/sakhadData/sakhadDataTypeCard";
import {
  useGetAllServiceListByType,
  useSakhadGetAllConsumptionInstructionListByShape,
  useSakhadGetAllConsumptionListByShape,
  useSakhadGetServiceByTypeAndCode,
} from "../../../hook/request/providerSakhad";
import Icon from "../../components/general/icon";
import TextField from "../../components/general/form/textField";
import { general } from "../../../store/action/general";
import SakhadConsumptionListModal from "../../components/sakhadData/SakhadConsumptionListModal";
import SakhadServiceDetailModal from "../../components/sakhadData/SakhadServiceDetailModal";

// --- Types ---
interface SakhadItem {
  title: string;
  icon: string;
  active: boolean;
  type: string;
}
type FormValues = {
  nationalNumber: string;
  consumptionShape: string;
  consumptionInstructionShape: string;
};
interface DisabledState {
  [key: string]: number;
}

// --- Constants ---
const LOCKOUT_DURATION = 60 * 60 * 1000;
const LOCAL_STORAGE_KEY = "sakhad_button_lockouts";

const SakhadDataArray: SakhadItem[] = [
  { title: "دارو", icon: "pills", active: true, type: "2" },
  { title: "پاراکلینیک", icon: "test", active: true, type: "3" },
  { title: "پرتوپزشکی", icon: "medical-kit", active: true, type: "4" },
  { title: "فیزیوتراپی", icon: "hospital", active: true, type: "6" },
  { title: "خدمات پزشکی", icon: "microscope", active: true, type: "7" },
  { title: "تجهیزات‌ پزشکی", icon: "stethoscope", active: true, type: "9" },
];

const ServiceGrid: FC<{
  disabledButtons: DisabledState;
  onItemClick: (item: SakhadItem) => void;
}> = ({ disabledButtons, onItemClick }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const getLockoutInfo = (type: string) => {
    const lastClick = disabledButtons[type];
    if (!lastClick) return { isLocked: false, text: "" };
    const remainingMs = LOCKOUT_DURATION - (Date.now() - lastClick);
    if (remainingMs <= 0) return { isLocked: false, text: "" };
    return { isLocked: true, text: `${Math.ceil(remainingMs / 60000)} دقیقه` };
  };

  return (
    <Row className="g-3">
      {SakhadDataArray.map((item) => {
        const { isLocked, text } = getLockoutInfo(item.type);
        return (
          <Col key={item.type} xs={12} md={6} lg={4}>
            <div
              className={`transition-all ${isLocked ? "opacity-75" : "hover-up"}`}
            >
              <SakhadDataTypeCard
                subTitle={isLocked ? `صبر کنید: ${text}` : "آماده به‌روزرسانی"}
                icon={item.icon}
                active={item.active && !isLocked}
                titleIcon={item}
                title={item.title}
                onClick={() => !isLocked && onItemClick(item)}
              />
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

const SakhadDataList: FC = () => {
  const dispatch = useDispatch();
  const [disabledButtons, setDisabledButtons] = useState<DisabledState>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const { setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      nationalNumber: "",
      consumptionShape: "",
      consumptionInstructionShape: "",
    },
  });

  // Mutation Hooks
  const getServicesListByType = useGetAllServiceListByType();
  const getSakhadServiceByTypeAndCode = useSakhadGetServiceByTypeAndCode();
  const getSakhadAllConsumptionListByShape =
    useSakhadGetAllConsumptionListByShape();
  const getSakhadAllConsumptionInstructionByShape =
    useSakhadGetAllConsumptionInstructionListByShape();

  const handleAction = (id: string) => {
    const vals = getValues();
    const openModal = (res: any) => {
      const finalData = res?.result || res?.data?.result || res?.data || [];

      dispatch(
        general.handleShowModal("sakhadConsumptionListModal", finalData),
      );
    };

    if (id === "01") {
      getSakhadServiceByTypeAndCode.mutate(
        { nationalNumber: vals.nationalNumber, type: "2" },
        {
          onSuccess: (res) => {
            dispatch(
              general.handleShowModal(
                "sakhadServiceDetailModal",
                res.data?.result || res.data,
              ),
            );
          },
        },
      );
    } else if (id === "02") {
      getSakhadAllConsumptionListByShape.mutate(
        { shape: vals.consumptionShape },
        { onSuccess: (res) => openModal(res) },
      );
    } else if (id === "03") {
      getSakhadAllConsumptionInstructionByShape.mutate(
        { shape: vals.consumptionInstructionShape },
        { onSuccess: (res) => openModal(res) },
      );
    }
  };

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(disabledButtons));
  }, [disabledButtons]);

  return (
    <div className="p-4 bg-light min-vh-100">
      <div className="mb-4 d-flex align-items-center gap-3 bg-white p-3 rounded-4 shadow-sm border-bottom border-primary border-3">
        <div className="bg-primary text-white p-2 rounded-3 shadow-sm">
          <Icon name="cloud-download" />
        </div>
        <div>
          <h5 className="m-0 fw-bold">مدیریت اطلاعات ساخد</h5>
          <p className="m-0 text-muted font-11">
            دریافت و همگام‌سازی اطلاعات از درگاه ساخد
          </p>
        </div>
      </div>

      <Card className="border-0 shadow-sm mb-5 rounded-4 overflow-hidden">
        <Card.Header className="bg-white py-3 border-bottom border-light">
          <h6 className="m-0 fw-bold text-primary">
            <Icon name="list" /> واکشی لیست کلی خدمات
          </h6>
        </Card.Header>
        <Card.Body className="bg-white p-4">
          <ServiceGrid
            disabledButtons={disabledButtons}
            onItemClick={(item) => {
              setDisabledButtons((p) => ({ ...p, [item.type]: Date.now() }));
              getServicesListByType.mutate({ pageNumber: 1, type: item.type });
            }}
          />
        </Card.Body>
      </Card>

      <Row className="g-4">
        {[
          {
            id: "01",
            title: "اطلاعات خدمت",
            name: "nationalNumber" as const,
            placeholder: "شناسه ملی...",
          },
          {
            id: "02",
            title: "زمان مصرف دارو",
            name: "consumptionShape" as const,
            placeholder: "کد شکل دارو...",
          },
          {
            id: "03",
            title: "میزان مصرف دارو",
            name: "consumptionInstructionShape" as const,
            placeholder: "کد شکل دارو...",
          },
        ].map((item) => (
          <Col key={item.id} xs={12} lg={4}>
            <Card className="border-0 shadow-sm h-100 rounded-4 p-4">
              <div className="d-flex align-items-center mb-4">
                <div className="badge bg-light text-dark p-2 rounded-3 me-3">
                  {item.id}
                </div>
                <h6 className="m-0 fw-bold text-secondary font-14">
                  {item.title}
                </h6>
              </div>
              <div className="modern-search-group">
                <TextField
                  name={item.name}
                  placeholder={item.placeholder}
                  onChange={(e) => setValue(item.name, e.target.value)}
                />
                <Button
                  variant="primary"
                  className="ms-2 search-btn"
                  onClick={() => handleAction(item.id)}
                >
                  <Icon name="search" />
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <style>{`
        .hover-up:hover { transform: translateY(-5px); transition: 0.3s; }
        .modern-search-group { display: flex; align-items: center; }
        .modern-search-group .inp-box { flex-grow: 1; margin: 0 !important; }
        .modern-search-group input { border-radius: 12px !important; border: 1px solid #e2e8f0 !important; height: 48px; background-color: #f8fafc; }
        .search-btn { border-radius: 12px !important; width: 50px; height: 48px; }
        .font-11 { font-size: 11px; }
      `}</style>

      <SakhadConsumptionListModal />
      <SakhadServiceDetailModal />
    </div>
  );
};

export default SakhadDataList;
